
import React from 'react';
import { LevelData, Boss, BudgetCategory } from './types';

// Helper to generate today's date string
const today = new Date().toISOString().split('T')[0];

export const INITIAL_CATEGORIES: BudgetCategory[] = [
  // --- NEEDS (~50%) ---
  { label: "Housing & Utilities", limit: 6000, actual: 0, color: '#2B8CEE' },
  { label: "Food (Groceries)", limit: 2500, actual: 0, color: '#10b981' },
  { label: "Transportation", limit: 1500, actual: 0, color: '#64748B' },
  { label: "Healthcare & Insurance", limit: 1200, actual: 0, color: '#ef4444' },
  
  // --- SAVINGS/DEBT (~20%) ---
  { label: "Emergency Fund", limit: 1000, actual: 0, color: '#f59e0b' },
  { label: "Retirement Savings", limit: 1500, actual: 0, color: '#ec4899' },
  { label: "Investments", limit: 1000, actual: 0, color: '#8b5cf6' },
  { label: "High-Interest Debt", limit: 500, actual: 0, color: '#b91c1c' },
  
  // --- WANTS (~30%) ---
  { label: "Dining Out & Entertainment", limit: 2000, actual: 0, color: '#001F3F' },
  { label: "Personal Care & Hobbies", limit: 1000, actual: 0, color: '#87CEEB' },
  { label: "Travel & Vacations", limit: 1000, actual: 0, color: '#0ea5e9' },
  { label: "Gifts & Charitable Giving", limit: 800, actual: 0, color: '#059669' },
];

export const INITIAL_LEVELS: LevelData[] = [
  {
    id: 1,
    title: "Survival",
    subtitle: "The Base Camp",
    description: "Secure your immediate existence. Stop the bleeding.",
    color: "bg-red-600",
    requirements: [
      { 
        id: "req_1_1", 
        label: "Monthly Surplus > R0", 
        met: false, 
        xpReward: 100,
        targetValue: 1, 
        currentValue: 0,
        unit: "R",
        history: [{ date: '2023-10-01', value: -500 }, { date: '2023-11-01', value: -200 }]
      },
      { 
        id: "req_1_2", 
        label: "Automate Bill Payments", 
        met: false, 
        xpReward: 50,
        targetValue: 1,
        currentValue: 0,
        unit: "Active",
        history: []
      },
      { 
        id: "req_1_3", 
        label: "Save R1,000 Starter Fund", 
        met: false, 
        xpReward: 150,
        targetValue: 1000,
        currentValue: 250,
        unit: "R",
        history: [{ date: '2023-11-15', value: 250 }]
      },
    ],
    rewards: ["Unlocks: Emergency Fund Tracker", "Badge: Survivor"],
    toolId: 'tool_emergency',
    toolName: 'Emergency Fund Tracker'
  },
  {
    id: 2,
    title: "Stability",
    subtitle: "The Shield",
    description: "Protect yourself from life's RNG.",
    color: "bg-orange-500",
    requirements: [
      { 
        id: "req_2_1", 
        label: "Employer 401k Match", 
        met: false, 
        xpReward: 200,
        targetValue: 100,
        currentValue: 0,
        unit: "%",
        history: []
      },
      { 
        id: "req_2_2", 
        label: "Pay off High-Interest Debt", 
        met: false, 
        xpReward: 300,
        targetValue: 100,
        currentValue: 20,
        unit: "% Paid",
        history: [{ date: '2023-08-01', value: 0 }, { date: '2023-12-01', value: 20 }]
      },
      { 
        id: "req_2_3", 
        label: "Save 3 Months Expenses", 
        met: false, 
        xpReward: 250,
        targetValue: 3,
        currentValue: 0.5,
        unit: "Months",
        history: [{ date: today, value: 0.5 }]
      },
    ],
    rewards: ["Unlocks: Investment Simulator", "Badge: Guardian"],
    toolId: 'tool_investment',
    toolName: 'The Time Machine'
  },
  {
    id: 3,
    title: "Growth",
    subtitle: "The Climb",
    description: "Build momentum and accumulate assets.",
    color: "bg-yellow-500",
    requirements: [
      { 
        id: "req_3_1", 
        label: "Credit Score > 720", 
        met: false, 
        xpReward: 150,
        targetValue: 720,
        currentValue: 680,
        unit: "Pts",
        history: [{ date: '2023-01-01', value: 650 }, { date: today, value: 680 }]
      },
      { 
        id: "req_3_2", 
        label: "Invest 15% of Income", 
        met: false, 
        xpReward: 200,
        targetValue: 15,
        currentValue: 5,
        unit: "%",
        history: []
      },
      { 
        id: "req_3_3", 
        label: "Max Out IRA (Yearly)", 
        met: false, 
        xpReward: 200,
        targetValue: 7000,
        currentValue: 1000,
        unit: "R",
        history: []
      },
    ],
    rewards: ["Unlocks: Portfolio Analyzer", "Badge: Climber"],
    toolId: 'tool_portfolio',
    toolName: 'Portfolio Analyzer'
  },
  {
    id: 4,
    title: "Freedom",
    subtitle: "The Summit",
    description: "Work becomes optional. Total independence.",
    color: "bg-emerald-500",
    requirements: [
      { 
        id: "req_4_1", 
        label: "Pay off Mortgage", 
        met: false, 
        xpReward: 500,
        targetValue: 100,
        currentValue: 15,
        unit: "%",
        history: []
      },
      { 
        id: "req_4_2", 
        label: "Net Worth > 25x Expenses", 
        met: false, 
        xpReward: 1000,
        targetValue: 25,
        currentValue: 2,
        unit: "x",
        history: []
      },
    ],
    rewards: ["Unlocks: Legacy Tools", "Badge: Sovereign"],
    toolId: 'tool_legacy',
    toolName: 'Legacy Blueprint'
  },
  {
    id: 5,
    title: "Legacy",
    subtitle: "The Sky",
    description: "Impact others and future generations.",
    color: "bg-blue-600",
    requirements: [
      { 
        id: "req_5_1", 
        label: "Create Estate Plan", 
        met: false, 
        xpReward: 300,
        targetValue: 1,
        currentValue: 0,
        unit: "Done",
        history: []
      },
      { 
        id: "req_5_2", 
        label: "Fund Education Trust", 
        met: false, 
        xpReward: 300,
        targetValue: 100000,
        currentValue: 0,
        unit: "R",
        history: []
      },
      { 
        id: "req_5_3", 
        label: "Philanthropy Goal", 
        met: false, 
        xpReward: 400,
        targetValue: 10000,
        currentValue: 0,
        unit: "R",
        history: []
      },
    ],
    rewards: ["Unlocks: Galactic Map", "Badge: Legend"],
    toolId: 'tool_legacy', // Linked to Legacy Planner as well
    toolName: 'Galactic Map'
  },
];

// Initial boss configuration for debt battle
export const INITIAL_BOSS: Boss = {
  id: "boss_car_loan",
  name: "The Car Loan Behemoth",
  totalHp: 150000, // Converted roughly to Rands context
  currentHp: 124500,
  color: "#ef4444"
};
